import React, { useState, useEffect } from 'react';
import { Pagination } from 'react-bootstrap';
import Swal from '../../../../helpers/swal';
import clinicbookingService from '../../../../../services/clinicbooking.service';
import { useParams } from 'react-router-dom';
import Loading from '../../../../common/Loading';
function BookingRequests() {
    const [bookingRequests, setBookingRequests] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const { clinicId } = useParams();

    useEffect(() => {
        getBookingRequests(currentPage, pageSize);
    }, [currentPage, pageSize, clinicId]);

    const getBookingRequests = (pageNo, size) => {
        setLoading(true);
        clinicbookingService
            .getBookingAppointmentRequests(clinicId, pageNo - 1, size)
            .then((res) => {
                setBookingRequests(res.data.content);
                setTotalPages(res.data.totalPages);
                setTotalElements(res.data.totalElements);
            })
            .catch((err) => {
                console.error('Error fetching booking requests', err);
            })
            .finally(() => setLoading(false));
    };

    const toggleRowExpansion = (index) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(index)) {
            newExpandedRows.delete(index);
        } else {
            newExpandedRows.add(index);
        }
        setExpandedRows(newExpandedRows);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const formatPhoneNumber = (phone) => {
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        }
        return phone;
    };

    const formatComments = (comments) => {
        if (!comments || comments.trim() === '') {
            return <span className="text-muted">No comments provided</span>;
        }
        return comments.split('\n').map((line, index) => (
            <span key={index}>
                {line}
                <br />
            </span>
        ));
    };

    const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        Swal.fire({
            icon: 'success',
            title: 'Copied!',
            text: `${label} copied to clipboard`,
            timer: 1500,
            showConfirmButton: false,
            position: 'top-end',
            toast: true,
        });
    };

    const getPatientTypeBadge = (isNewPatient) =>
        isNewPatient ? 'clinic-booking-patient-badge clinic-booking-patient-badge--new' : 'clinic-booking-patient-badge clinic-booking-patient-badge--existing';

    const startItem = bookingRequests.length > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const endItem = Math.min(currentPage * pageSize, totalElements);

    return (
        <>
            <div className="clinic-booking-toolbar">
                <div className="d-flex align-items-center gap-2">
                    <span>Show</span>
                    <select
                        className="form-select form-select-sm clinic-booking-page-size"
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        disabled={loading}
                    >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                    </select>
                    <span>entries</span>
                </div>
                <div>
                    Total: <strong>{totalElements}</strong>
                </div>
            </div>

            {loading ? (
                <Loading />
            ) : (
                <>
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0 process-queue-table clinic-booking-table">
                            <thead>
                                <tr>
                                    <th>
                                        <i className="bi bi-person-badge"></i> Doctor
                                    </th>
                                    <th>
                                        <i className="bi bi-person"></i> Patient
                                    </th>
                                    <th>
                                        <i className="bi bi-telephone"></i> Phone
                                    </th>
                                    <th>
                                        <i className="bi bi-envelope"></i> Email
                                    </th>
                                    <th>
                                        <i className="bi bi-calendar-plus"></i> Created
                                    </th>
                                    <th>
                                        <i className="bi bi-activity"></i> Status
                                    </th>
                                    <th className="text-end">
                                        <i className="bi bi-gear"></i> Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {bookingRequests.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="p-0 border-0">
                                            <div className="clinic-booking-empty">
                                                <i className="bi bi-calendar-x"></i>
                                                No booking requests found.
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    bookingRequests.map((request, index) => (
                                        <React.Fragment key={index}>
                                            <tr className="clinic-booking-row" style={{ cursor: 'pointer' }} onClick={() => toggleRowExpansion(index)}>
                                                <td>
                                                    <span className="clinic-booking-name">{request.doctorName}</span>
                                                </td>
                                                <td>
                                                    <div className="clinic-booking-patient-cell">
                                                        <span className="clinic-booking-name">
                                                            {request.firstName} {request.lastName}
                                                            {request.preferredName && (
                                                                <span className="clinic-booking-meta"> ({request.preferredName})</span>
                                                            )}
                                                        </span>
                                                        <span className={getPatientTypeBadge(request.isNewPatient)}>
                                                            {request.isNewPatient ? 'New' : 'Existing'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="clinic-booking-meta">{formatPhoneNumber(request.cellPhone)}</td>
                                                <td>
                                                    <a href={`mailto:${request.email}`} onClick={(e) => e.stopPropagation()} className="clinic-booking-meta">
                                                        {request.email}
                                                    </a>
                                                </td>
                                                <td className="clinic-booking-meta">{request.createdAt}</td>
                                                <td>
                                                    <span
                                                        className={`clinic-booking-status-badge ${
                                                            request.status
                                                                ? 'clinic-booking-status-badge--queued'
                                                                : 'clinic-booking-status-badge--processed'
                                                        }`}
                                                    >
                                                        {request.status ? 'Queued' : 'Processed'}
                                                    </span>
                                                </td>
                                                <td className="text-end" onClick={(e) => e.stopPropagation()}>
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => toggleRowExpansion(index)}
                                                        title={expandedRows.has(index) ? 'Collapse' : 'Expand'}
                                                    >
                                                        <i className={`bi ${expandedRows.has(index) ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                                                    </button>
                                                </td>
                                            </tr>
                                            {expandedRows.has(index) && (
                                                <tr>
                                                    <td colSpan="7" className="p-0 border-0">
                                                        <div className="clinic-booking-detail-panel">
                                                            <div className="clinic-booking-detail-grid">
                                                                <div className="notifications-panel">
                                                                    <div className="notifications-panel__header">
                                                                        <i className="bi bi-calendar-check"></i>
                                                                        Time Preferences
                                                                    </div>
                                                                    <div className="notifications-panel__body">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">First Preference</span>
                                                                            <span className="notifications-info-value">
                                                                                {request.firstPreferredDate} · {request.firstPreferredTime}
                                                                            </span>
                                                                        </div>
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Second Preference</span>
                                                                            <span className="notifications-info-value">
                                                                                {request.secondPreferredDate} · {request.secondPreferredTime}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="notifications-panel">
                                                                    <div className="notifications-panel__header">
                                                                        <i className="bi bi-chat-dots"></i>
                                                                        Visit Information
                                                                    </div>
                                                                    <div className="notifications-panel__body">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Reason</span>
                                                                            <span className="notifications-info-value">{request.reason || '—'}</span>
                                                                        </div>
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Comments</span>
                                                                            <div className="clinic-booking-comments">{formatComments(request.comments)}</div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="notifications-panel">
                                                                    <div className="notifications-panel__header">
                                                                        <i className="bi bi-person-badge"></i>
                                                                        Identification
                                                                    </div>
                                                                    <div className="notifications-panel__body">
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Clinic ID</span>
                                                                            <div className="notifications-info-value d-flex align-items-center gap-2">
                                                                                <span className="font-monospace small">{request.clinicId}</span>
                                                                                <button type="button" className="clinic-copy-btn" onClick={() => copyToClipboard(request.clinicId, 'Clinic ID')} title="Copy">
                                                                                    <i className="bi bi-copy"></i>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Doctor ID</span>
                                                                            <div className="notifications-info-value d-flex align-items-center gap-2">
                                                                                <span className="font-monospace small">{request.bookingDoctorId}</span>
                                                                                <button type="button" className="clinic-copy-btn" onClick={() => copyToClipboard(request.bookingDoctorId, 'Doctor ID')} title="Copy">
                                                                                    <i className="bi bi-copy"></i>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        <div className="notifications-info-row mb-0">
                                                                            <span className="notifications-info-label">Patient Type</span>
                                                                            <span className={getPatientTypeBadge(request.isNewPatient)}>
                                                                                {request.isNewPatient ? 'New Patient' : 'Existing Patient'}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {bookingRequests.length > 0 && (
                        <div className="clinic-booking-footer">
                            <div>
                                Showing {startItem} to {endItem} of {totalElements} entries
                            </div>
                            {totalPages > 1 && (
                                <Pagination className="mb-0">
                                    <Pagination.First disabled={currentPage === 1} onClick={() => handlePageChange(1)} />
                                    <Pagination.Prev disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} />
                                    {[...Array(totalPages)].map((_, index) => {
                                        const page = index + 1;
                                        if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                                            return (
                                                <Pagination.Item key={page} active={page === currentPage} onClick={() => handlePageChange(page)}>
                                                    {page}
                                                </Pagination.Item>
                                            );
                                        }
                                        if (page === currentPage - 2 || page === currentPage + 2) {
                                            return <Pagination.Ellipsis key={page} />;
                                        }
                                        return null;
                                    })}
                                    <Pagination.Next disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} />
                                    <Pagination.Last disabled={currentPage === totalPages} onClick={() => handlePageChange(totalPages)} />
                                </Pagination>
                            )}
                        </div>
                    )}
                </>
            )}
        </>
    );
}

export default BookingRequests;
